import React, {lazy} from 'react';
import {CardDataConsumer} from 'Components/common/Context/CardDataContext';
import {CONFIG_DATA_BASE_KEYS, NAV_MAP_DEFAULT_CONFIG} from 'Utils/constants';
import IMDF_FEATURE_TYPES from 'Utils/imdfFeatureTypes';

const JourneyCardContainer = lazy(() =>
    import('Components/Explorer/Cards/JourneyCard/JourneyCardContainer')
);

const EventDetailsCardContainer = lazy(() =>
    import('Components/Explorer/Cards/EventDetails/EventDetailsCardContainer')
);
const EventsListContainer = lazy(() =>
    import('Components/Explorer/Cards/EventsList/EventsListContainer')
);
const SearchCardContainer = lazy(() =>
    import('Components/Explorer/Cards/SearchCard/SearchCardContainer')
);
const DirectionsContainer = lazy(() =>
    import('Components/Explorer/Cards/Directions/DirectionsContainer')
);
const TenantsList = lazy(() => import('Components/common/Tenants/TenantsList'));
const PromotionsListContainer = lazy(() =>
    import('Components/Explorer/Cards/PromotionsList/PromotionsListContainer')
);
const PromotionsDetailsCardContainer = lazy(() =>
    import('Components/Explorer/Cards/PromotionDetails/PromotionDetailsCardContainer')
);
const SelectedLocationCardContainer = lazy(() =>
    import('Components/Explorer/Cards/SelectedLocation/SelectedLocationCardContainer')
);
const LocationDetailsContainer = lazy(() =>
    import('./Cards/LocationDetails/LocationDetailsContainer')
);

const CARD_COMPONENTS = {
    searchCard: (
        <CardDataConsumer>
            {({mapCenter, mapDataForKiosk, config, scanLocation}) => (
                <SearchCardContainer
                    mapCenter={mapCenter}
                    mapBoundaryRadius={mapDataForKiosk.navMap.config.mapBoundaryRadius}
                    defaultRotation={
                        mapDataForKiosk.navMap.config.defaultRotation ??
                        NAV_MAP_DEFAULT_CONFIG.defaultRotation
                    }
                    // here we filter out items we dont want to show in search
                    features={mapDataForKiosk.navMap.features.filter(
                        ({properties: {hideFromSearch, isDisplayPoint, locationId, groupId}}) =>
                            !isDisplayPoint && !hideFromSearch && locationId && !groupId
                    )}
                    suggestedLocations={
                        config.signConfig[CONFIG_DATA_BASE_KEYS.WITH_INHERITANCE].suggestedLocations
                            ?.locationArray
                    }
                    parkingStatusMapping={
                        config.signConfig[CONFIG_DATA_BASE_KEYS.WITH_INHERITANCE]
                            ?.parkingStatusMapping
                    }
                    selectedItemPanel={
                        // This component cannot be imported inside SearchCard because it will throw cyclic dependency
                        <SelectedLocationCardContainer
                            config={config}
                            currentKioskFeature={scanLocation}
                        />
                    }
                />
            )}
        </CardDataConsumer>
    ),
    directoryCard: (
        <CardDataConsumer>
            {({mapDataForKiosk, config, setClearFilterHandler}) => {
                // If The directoryContentSource defined is disabled in current scene
                // pick the first Venue feature from the navmap as default contentSource

                let directoryContentSource = {};

                const selectedDefaultContentSource =
                    config.signConfig[CONFIG_DATA_BASE_KEYS.WITH_INHERITANCE].menuTab
                        ?.directoryContentSource &&
                    // check if its available in scene
                    mapDataForKiosk.navMap.features.find(
                        feature =>
                            feature.properties.featureId ===
                            config.signConfig[CONFIG_DATA_BASE_KEYS.WITH_INHERITANCE].menuTab
                                ?.directoryContentSource
                    );

                // if we have something default, verify it has tenants
                if (
                    selectedDefaultContentSource &&
                    mapDataForKiosk.tenantFeatures.find(feature =>
                        feature.properties.contentSources.find(
                            f =>
                                f.properties.featureId ===
                                selectedDefaultContentSource.properties.featureId
                        )
                    )
                ) {
                    directoryContentSource = selectedDefaultContentSource;
                } else {
                    // if identified default source doesn't have tenants...
                    // pick up the first venue feature
                    directoryContentSource = mapDataForKiosk.navMap.features.find(
                        f =>
                            f.properties?.featureType?.imdfFeatureType?.code ===
                            IMDF_FEATURE_TYPES.venue.code
                    );
                }

                return (
                    <TenantsList
                        directoryContentSource={directoryContentSource}
                        tenantFeatures={mapDataForKiosk.tenantFeatures}
                        currentKioskFeature={mapDataForKiosk.currentKioskFeature}
                        contentSources={mapDataForKiosk.navMap.features.filter(
                            f => f.properties.isContentSource
                        )}
                        setClearFilterHandler={setClearFilterHandler}
                    />
                );
            }}
        </CardDataConsumer>
    ),
    featureSummaryCard: (
        <CardDataConsumer>
            {({config}) => <SelectedLocationCardContainer config={config} />}
        </CardDataConsumer>
    ),
    featureDetailsCard: (
        <CardDataConsumer>
            {({mapDataForKiosk, navigationHistory, navMode, config}) => (
                <LocationDetailsContainer
                    currentKioskFeature={mapDataForKiosk.currentKioskFeature}
                    parkingStatusMapping={
                        config.signConfig[CONFIG_DATA_BASE_KEYS.WITH_INHERITANCE]
                            ?.parkingStatusMapping
                    }
                    contentSources={mapDataForKiosk.navMap.features.filter(
                        f => f.properties.isContentSource
                    )}
                    tenantFeatures={mapDataForKiosk.tenantFeatures}
                    navigationHistory={navigationHistory}
                    navMode={navMode}
                    signConfig={config.signConfig}
                />
            )}
        </CardDataConsumer>
    ),
    directions: (
        <CardDataConsumer>
            {({mapDataForKiosk, config, mapCenter}) => (
                <DirectionsContainer
                    mapDataForKiosk={mapDataForKiosk}
                    mapCenter={mapCenter}
                    mapBoundaryRadius={mapDataForKiosk.navMap.config.mapBoundaryRadius}
                    // here we filter out items we dont want to show in search
                    features={mapDataForKiosk.navMap.features.filter(
                        ({properties: {hideFromSearch, isDisplayPoint, locationId, groupId}}) =>
                            !isDisplayPoint && !hideFromSearch && locationId && !groupId
                    )}
                    defaultRotation={
                        mapDataForKiosk.navMap.config.defaultRotation ??
                        NAV_MAP_DEFAULT_CONFIG.defaultRotation
                    }
                    config={config}
                    suggestedLocations={
                        config.signConfig[CONFIG_DATA_BASE_KEYS.WITH_INHERITANCE].suggestedLocations
                            ?.locationArray
                    }
                />
            )}
        </CardDataConsumer>
    ),
    journeyCard: (
        <CardDataConsumer>
            {({mapDataForKiosk, config, scanLocation}) => (
                <JourneyCardContainer
                    mapDataForKiosk={mapDataForKiosk}
                    config={config}
                    currentKioskFeature={scanLocation}
                />
            )}
        </CardDataConsumer>
    ),
    eventsList: (
        <CardDataConsumer>
            {({mapDataForKiosk, config, setClearFilterHandler}) => {
                // If The directoryContentSource defined is disabled in current scene
                // pick the first Venue feature from the navmap as default contentSource

                let directoryContentSource = {};

                const selectedDefaultContentSource =
                    config.signConfig[CONFIG_DATA_BASE_KEYS.WITH_INHERITANCE].menuTab
                        ?.directoryContentSource &&
                    // check if its available in scene
                    mapDataForKiosk.navMap.features.find(
                        feature =>
                            feature.properties.featureId ===
                            config.signConfig[CONFIG_DATA_BASE_KEYS.WITH_INHERITANCE].menuTab
                                ?.directoryContentSource
                    );

                // if we have something default, verify it has tenants
                if (
                    selectedDefaultContentSource &&
                    mapDataForKiosk.tenantFeatures.find(feature =>
                        feature.properties.contentSources.find(
                            f =>
                                f.properties.featureId ===
                                selectedDefaultContentSource.properties.featureId
                        )
                    )
                ) {
                    directoryContentSource = selectedDefaultContentSource;
                } else {
                    // if identified default source doesn't have tenants...
                    // pick up the first venue feature
                    directoryContentSource = mapDataForKiosk.navMap.features.find(
                        f =>
                            f.properties?.featureType?.imdfFeatureType?.code ===
                            IMDF_FEATURE_TYPES.venue.code
                    );
                }

                return (
                    <EventsListContainer
                        contentSources={mapDataForKiosk.navMap.features.filter(
                            f => f.properties.isContentSource
                        )}
                        directoryContentSource={directoryContentSource}
                        mapDataForKiosk={mapDataForKiosk}
                        config={config}
                        events={mapDataForKiosk.events}
                        setClearFilterHandler={setClearFilterHandler}
                    />
                );
            }}
        </CardDataConsumer>
    ),
    eventDetailsCard: (
        <CardDataConsumer>
            {({mapDataForKiosk}) => (
                <EventDetailsCardContainer
                    currentKioskFeature={mapDataForKiosk.currentKioskFeature}
                    events={mapDataForKiosk.events}
                />
            )}
        </CardDataConsumer>
    ),
    promotionsList: (
        <CardDataConsumer>
            {({mapDataForKiosk, config, setClearFilterHandler}) => {
                return (
                    <PromotionsListContainer
                        mapDataForKiosk={mapDataForKiosk}
                        config={config}
                        setClearFilterHandler={setClearFilterHandler}
                    />
                );
            }}
        </CardDataConsumer>
    ),
    promotionsDetailsCard: (
        <CardDataConsumer>
            {({mapDataForKiosk}) => {
                return (
                    <PromotionsDetailsCardContainer
                        tenantFeatures={mapDataForKiosk.tenantFeatures}
                    />
                );
            }}
        </CardDataConsumer>
    ),
};

// @see KIOSK_ICONS to add cardName to an icon, then add the component you want to display in CARD_COMPONENTS
export const getCardComponent = cardName => CARD_COMPONENTS?.[cardName];
